import { Box, Container, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const dataPrivacyRef = useRef<HTMLDivElement>(null);

  // Scroll when link clicked
  const scrollToDataPrivacy = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // Scroll to target
    dataPrivacyRef.current?.scrollIntoView({ behavior: "smooth" });

    // Update URL without reloading
    window.history.pushState(null, "", "#dataPrivacy");
  };

  // Scroll automatically if URL has hash
  useEffect(() => {
    if (window.location.hash === "#dataPrivacy") {
      dataPrivacyRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);
  return (
    <Container maxWidth="lg">
      <Box mt={{ xs: "60px", md: "80px" }}>
        <Typography
          color={"#520A76"}
          component={"h1"}
          fontSize={{ xs: 24, md: 40 }}
          fontWeight={700}
        >
          {t("Privacy Policy")}
        </Typography>
        <Typography component={"p"}>
          {t("Privacy Policy Description")}
        </Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Types of Information We Collect")}
        </Typography>
        <Typography component={"p"}>
          {t("Types of Information We Collect Description")}
        </Typography>

        <ol>
          <li>{t("Collect Your name")}</li>
          <li>{t("Collect Date of Birth")}</li>
          <li>{t("Collect Country")}</li>
          <li>{t("Collect Gender")}</li>
          <li>{t("Collect Marital Status")}</li>
          <li>{t("Collect Home, work, or email addresses")}</li>
          <li>{t("Collect Telephone and fax numbers")}</li>
          <li>{t("Collect Nationality")}</li>
          <li>
            {t("Collect ID, Passport, Residence, Driver License or Work Card")}
          </li>
          <li>
            {t(
              "Collect Other information necessary to enhance your experience"
            )}
          </li>
        </ol>

        <Typography component={"p"}>{t("Collect Also")}</Typography>
        <ol>
          <li>{t("Collect Your use of our websites or application")}</li>
          <li>{t("Collect Communication preferences")}</li>
          <li>{t("Collect Responses to promotional offers and surveys")}</li>
        </ol>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Purpose for Collection and Use")}
        </Typography>
        <Typography component={"p"}>
          {t("Purpose for Collection and Use Description")}
        </Typography>
        <ol>
          <li>{t("Purpose A")}</li>
          <li>{t("Purpose B")}</li>
          <li>{t("Purpose C")}</li>
        </ol>
        <Typography component={"p"}>{t("Purpose Last")}</Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Purpose of Processing and Disclosure")}
        </Typography>
        <Typography component={"p"}>
          {t("Purpose of Processing and Disclosure Description")}
        </Typography>
        <ol>
          <li>{t("Processing A")}</li>
          <li>{t("Processing B")}</li>
          <li>{t("Processing C")}</li>
          <li>{t("Processing D")}</li>
          <li>{t("Processing E")}</li>
          <li>{t("Processing F")}</li>
        </ol>
        <Typography component={"p"}>{t("Processing Last")}</Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Consent")}
        </Typography>
        <Typography component={"p"}>{t("Consent Description")}</Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Access to personally identifiable information")}
        </Typography>
        <Typography component={"p"}>
          {t("Access to personally identifiable information Description")}
        </Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Cookies and tracking technologies")}
        </Typography>
        <Typography component={"p"} mb={2}>
          {t("Cookies and tracking technologies Description A")}
        </Typography>
        <Typography component={"p"} mb={2}>
          {t("Cookies and tracking technologies Description B")}
        </Typography>
        <Typography component={"p"}>
          {t("Cookies and tracking technologies Description C")}
        </Typography>
        <ul>
          <li>{t("Cookies and tracking technologies Description list A")}</li>
          <li>{t("Cookies and tracking technologies Description list B")}</li>
          <li>{t("Cookies and tracking technologies Description list C")}</li>
          <li>{t("Cookies and tracking technologies Description list D")}</li>
        </ul>
        <Typography component={"p"} mb={2}>
          {t("Cookies and tracking technologies Description D")}
        </Typography>
        <Typography component={"p"} mb={2}>
          {t("Cookies and tracking technologies Description E")}
        </Typography>
        <Typography component={"p"}>
          {t("Cookies and tracking technologies Description F")}
        </Typography>
      </Box>
      <Box
        mt={{ xs: "24px", md: "40px" }}
        id="dataPrivacy"
        ref={dataPrivacyRef}
        sx={{ scrollMarginTop: "100px" }}
      >
        <Typography
          component="h2"
          color="#520A76"
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Data Privacy Statement")}
        </Typography>
        <Typography component="p" mt={0.5}>
          {t("Data Privacy Statement Description")}
        </Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Types of Data")}
        </Typography>
        <Typography component={"p"} mb={2} mt={0.5}>
          {t("Types of Data Description")}
        </Typography>
        <ol>
          <li>
            {t("Login Information")}
            <ul style={{ listStyleType: "disc" }}>
              <li style={{ margin: "4px 0" }}>
                {t("Login Information Description")}
              </li>
            </ul>
          </li>
          <li>
            {t("Operational Data")}
            <ul style={{ listStyleType: "disc" }}>
              <li style={{ margin: "4px 0 4px 0" }}>
                {t("Operational Data Description")}
              </li>
            </ul>
          </li>
          <li>{t("Location Data Access")}</li>
          <ul style={{ listStyleType: "disc" }}>
            <li style={{ margin: "4px 0 4px 0" }}>
              {t("Location Data Access Description")}
            </li>
            <ul>
              <li style={{ margin: "4px 0 4px 0" }}>{t("Monitoring")}</li>
            </ul>
            <ul>
              <li style={{ margin: "4px 0 4px 0" }}>{t("Optimizing")}</li>
            </ul>
            <ul>
              <li style={{ margin: "4px 0 4px 0" }}>{t("Maintaining")}</li>
            </ul>
          </ul>
        </ol>
        {t("Location tracking")}
        <ul>
          <li>{t("consent")}</li>
        </ul>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Image Data")}
        </Typography>
        <Typography component={"p"} mt={0.5}>
          {t("Image Data Description")}
        </Typography>
        <ul>
          <li>{t("Vehicle Verification")}</li>
        </ul>
        <ul>
          <li>{t("Meter Verification")}</li>
        </ul>
        <ul>
          <li>{t("Other Verifications")}</li>
        </ul>
        {t(
          "These images are used for verification purposes only and are stored securely."
        )}
      </Box>
      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("How We Use Your Data")}
        </Typography>
        <Typography component={"p"} mt={0.5}>
          {t("How We Use Your Data Description")}
        </Typography>
        <ul>
          <li>{t("Managing and assigning shifts")}</li>
        </ul>
        <ul>
          <li>{t("Providing location tracking during active shifts")}</li>
        </ul>
        <ul>
          <li>{t("Maintaining accurate shift activity records")}</li>
        </ul>
        <ul>
          <li>{t("Verification purposes")}</li>
        </ul>
        <ul>
          <li>{t("Improving the app experience")}</li>
        </ul>
      </Box>
      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Data Sharing Policy")}
        </Typography>
        <Typography component={"p"} mt={0.5}>
          {t("Data Sharing Policy Description")}
        </Typography>
      </Box>
      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Secure Data Processing")}
        </Typography>
        <Typography component={"p"} mt={0.5}>
          {t("Secure Data Processing Description")}
        </Typography>
      </Box>
      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Guidelines for Data Retention and Deletion")}
        </Typography>
        <ul>
          <li>{t("process")}</li>
        </ul>
        <ul>
          <li>{t("data storage")}</li>
        </ul>
        <ul>
          <li>{t("Location data")}</li>
        </ul>
        <ul>
          <li>{t("Image data")}</li>
        </ul>
      </Box>
      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Access to This Privacy Policy")}
        </Typography>
        <Typography component={"p"} mt={0.5}>
          {t("This privacy policy is publicly accessible at")}{" "}
          <a
            href="#dataPrivacy"
            onClick={scrollToDataPrivacy}
            style={{ color: "#520A76", textDecoration: "underline" }}
          >
            https://limousine.iyelo.com/en/privacy
          </a>
          .{" "}
          {t(
            "It is also available within the app under Profile → Settings → Privacy Policy for easy reference."
          )}
        </Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Links to other websites")}
        </Typography>
        <Typography component={"p"}>
          {t("Links to other websites Description")}
        </Typography>
      </Box>
      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Minors privacy")}
        </Typography>
        <Typography component={"p"}>
          {t("Minors privacy Description")}
        </Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Accuracy and privacy")}
        </Typography>
        <Typography component={"p"}>
          {t("Accuracy and privacy Description")}
        </Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Changes to This Privacy Policy")}
        </Typography>
        <Typography component={"p"}>
          {t("Changes to This Privacy Policy Description")}
        </Typography>
      </Box>

      <Box mt={{ xs: "24px", md: "40px" }}>
        <Typography
          component={"h2"}
          color={"#520A76"}
          fontSize={{ xs: 20, md: 24 }}
          fontWeight={600}
        >
          {t("Contact Us")}
        </Typography>
        <Typography component={"p"}>
          {t("Contact Us Description")}{" "}
          <a href="mailto:ccc@iyelo.com" style={{ color: "#520A76" }}>
            ccc@iyelo.com
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
